/**
 * Simple If/Else and list builder for email templates.
 * Aimed at non-technical HR — one field, then/otherwise, no rule jargon.
 */
import React, { useMemo, useState } from 'react';
import {
  EyeOff, GitBranch, ListOrdered,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  buildIfBlock,
  buildListLogicBlock,
  defaultItemVariableForList,
  defaultThenHtmlForVariable,
  isJinjaIdentifier,
  isListLikeVariable,
} from '@/lib/emailTemplateLogic';
import { labelForVariable } from '@/lib/emailTemplateVars';
import { toEditorHtml } from '@/lib/emailTemplateJinjaDom';

const LIST_FORMATS = [
  {
    value: 'ol',
    label: 'Numbered list',
    example: '1. First question\n2. Second question',
  },
  {
    value: 'ul',
    label: 'Bullet list',
    example: '• First question\n• Second question',
  },
  {
    value: 'plain',
    label: 'One line per item',
    example: 'First question\nSecond question',
  },
];

function resolveField({ variable, customVar }) {
  const custom = String(customVar || '').trim();
  if (custom) return custom;
  return String(variable || '').trim();
}

function FieldPicker({
  testId,
  options,
  variable,
  customVar,
  onVariableChange,
  onCustomChange,
  label,
  hint,
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-foreground">{label}</Label>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      {options.length > 0 && (
        <Select
          value={variable || undefined}
          onValueChange={onVariableChange}
        >
          <SelectTrigger className="h-9 text-sm" data-testid={testId}>
            <SelectValue placeholder="Choose a field…" />
          </SelectTrigger>
          <SelectContent>
            {options.map((v) => (
              <SelectItem key={v} value={v} className="text-sm">
                <span className="font-medium">{labelForVariable(v)}</span>
                <span className="ml-1.5 text-[11px] text-muted-foreground">{v}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Input
        placeholder="Or type a field name (e.g. join_url)"
        value={customVar}
        onChange={(e) => onCustomChange(e.target.value)}
        className="h-9 text-sm"
        data-testid={`${testId}-custom`}
      />
    </div>
  );
}

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   variables?: string[],
 *   selectedHtml?: string,
 *   onInsert: (editorHtml: string) => void,
 *   testId?: string,
 * }} props
 */
export default function EmailLogicBuilderDialog({
  open,
  onOpenChange,
  variables = [],
  selectedHtml = '',
  onInsert,
  testId = 'email-html-editor',
}) {
  const options = useMemo(
    () => [...new Set(variables.filter(Boolean))].sort(),
    [variables],
  );

  const defaultVar = options.includes('join_url')
    ? 'join_url'
    : options.includes('questions')
      ? 'questions'
      : (options[0] || '');

  const [mode, setMode] = useState('if');
  const [variable, setVariable] = useState(defaultVar);
  const [customVar, setCustomVar] = useState('');
  const [thenText, setThenText] = useState('');
  const [elseText, setElseText] = useState('');
  const [listVariable, setListVariable] = useState(
    options.includes('questions') ? 'questions' : defaultVar,
  );
  const [listCustomVar, setListCustomVar] = useState('');
  const [listFormat, setListFormat] = useState('ol');
  const [listEmptyText, setListEmptyText] = useState(
    'No questions included for this briefing.',
  );
  const [error, setError] = useState('');

  const resetForm = (seedVar = defaultVar) => {
    const hasSelection = Boolean(String(selectedHtml || '').trim());
    const preferList = !hasSelection && (seedVar === 'questions' || isListLikeVariable(seedVar));
    const ifSeed = options.includes('join_url') ? 'join_url' : seedVar;
    const listSeed = options.includes('questions') ? 'questions' : seedVar;

    setMode(preferList ? 'list' : 'if');
    setVariable(preferList ? listSeed : ifSeed);
    setCustomVar('');
    setThenText(hasSelection ? selectedHtml : '');
    setElseText(
      preferList
        ? ''
        : (ifSeed === 'join_url' ? 'Link not available yet.' : ''),
    );
    setListVariable(listSeed);
    setListCustomVar('');
    setListFormat('ol');
    setListEmptyText(
      listSeed === 'questions'
        ? 'No questions included for this briefing.'
        : 'Nothing to show here.',
    );
    setError('');
  };

  const handleOpenChange = (next) => {
    if (next) resetForm(defaultVar);
    onOpenChange(next);
  };

  const insert = () => {
    try {
      let html;
      if (mode === 'list') {
        const list = resolveField({ variable: listVariable, customVar: listCustomVar });
        if (!isJinjaIdentifier(list)) {
          setError('Choose or type a list field name (letters, numbers, _).');
          return;
        }
        html = toEditorHtml(buildListLogicBlock({
          listVariable: list,
          itemVariable: defaultItemVariableForList(list),
          format: listFormat,
          emptyHtml: String(listEmptyText || '').trim(),
          wrapInIf: true,
        }));
      } else {
        const field = resolveField({ variable, customVar });
        if (!isJinjaIdentifier(field)) {
          setError('Choose a field to check (e.g. join_url).');
          return;
        }
        const thenHtml = String(thenText || '').trim()
          || defaultThenHtmlForVariable(field, selectedHtml);
        html = toEditorHtml(buildIfBlock({
          variable: field,
          thenHtml,
          elseHtml: String(elseText || '').trim(),
        }));
      }
      onInsert(html);
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || 'Could not build this block');
    }
  };

  const formatExample = LIST_FORMATS.find((f) => f.value === listFormat)?.example || '';
  const activeField = resolveField({ variable, customVar });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-xl overflow-y-auto sm:max-w-xl"
        data-testid={`${testId}-logic-popover`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <GitBranch className="h-4 w-4 text-primary" />
            Build dynamic content
          </DialogTitle>
          <DialogDescription className="text-sm">
            Describe what should appear in the email using plain language.
            HireIQ turns your choices into the template automatically — no coding needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium text-foreground">What do you want to do?</Label>
            <RadioGroup
              value={mode}
              onValueChange={setMode}
              className="grid gap-2 sm:grid-cols-2"
              data-testid={`${testId}-logic-mode`}
            >
              <Label
                className={cn(
                  'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-sm font-normal',
                  mode === 'if' && 'border-primary bg-primary/5',
                )}
              >
                <span className="flex items-center gap-2 font-medium text-foreground">
                  <RadioGroupItem value="if" id={`${testId}-mode-if`} />
                  <EyeOff className="h-3.5 w-3.5 text-primary" />
                  Show different content
                </span>
                <span className="pl-6 text-[11px] text-muted-foreground">
                  If a field has a value, show one message — otherwise show another.
                </span>
              </Label>
              <Label
                className={cn(
                  'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-sm font-normal',
                  mode === 'list' && 'border-primary bg-primary/5',
                )}
              >
                <span className="flex items-center gap-2 font-medium text-foreground">
                  <RadioGroupItem value="list" id={`${testId}-mode-list`} />
                  <ListOrdered className="h-3.5 w-3.5 text-primary" />
                  Repeat for each item
                </span>
                <span className="pl-6 text-[11px] text-muted-foreground">
                  Turn a list (questions, skills, panellists) into a readable checklist.
                </span>
              </Label>
            </RadioGroup>
          </div>

          {mode === 'if' ? (
            <div className="space-y-3">
              <p className="rounded-md bg-secondary/50 px-3 py-2 text-[11px] text-muted-foreground">
                Example: If <strong>Meeting join link</strong> has a value, show the link.
                Otherwise show “Link not available yet.”
              </p>

              <div
                className="space-y-3 rounded-lg border border-l-4 border-l-primary bg-white p-3 shadow-sm"
                data-testid={`${testId}-if-block`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  If
                </p>

                <FieldPicker
                  testId={`${testId}-logic-variable`}
                  options={options}
                  variable={variable}
                  customVar={customVar}
                  label="When this field has a value…"
                  hint="Pick the email data field you want to check."
                  onVariableChange={(v) => {
                    setVariable(v);
                    setCustomVar('');
                    if (v === 'join_url' && !elseText.trim()) {
                      setElseText('Link not available yet.');
                    }
                  }}
                  onCustomChange={setCustomVar}
                />

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-foreground">
                    Then show this in the email
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Plain text is fine. Leave blank to insert the field value automatically
                    {isListLikeVariable(activeField)
                      ? ' (lists become a numbered checklist).'
                      : '.'}
                  </p>
                  <Textarea
                    rows={3}
                    value={thenText}
                    onChange={(e) => setThenText(e.target.value)}
                    placeholder="e.g. Join the meeting using the link below."
                    className="text-sm"
                    data-testid={`${testId}-logic-then`}
                  />
                </div>
              </div>

              <div
                className="space-y-2 rounded-lg border border-dashed border-l-4 border-l-muted-foreground/40 p-3"
                data-testid={`${testId}-else-block`}
              >
                <Label className="text-xs font-medium text-foreground">
                  Otherwise — show this instead
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Used when the field is empty or missing. Leave blank to show nothing.
                </p>
                <Textarea
                  rows={2}
                  value={elseText}
                  onChange={(e) => setElseText(e.target.value)}
                  placeholder="e.g. Link not available yet."
                  className="text-sm"
                  data-testid={`${testId}-logic-else`}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="rounded-md bg-secondary/50 px-3 py-2 text-[11px] text-muted-foreground">
                Example: List each <strong>Interview question</strong> as 1, 2, 3…
                If there are no questions, show a short note instead.
              </p>

              <div className="space-y-3 rounded-lg border border-l-4 border-l-primary bg-white p-3 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  For each item
                </p>

                <FieldPicker
                  testId={`${testId}-logic-list`}
                  options={options}
                  variable={listVariable}
                  customVar={listCustomVar}
                  label="Which list should we repeat?"
                  hint="Choose a collection such as interview questions."
                  onVariableChange={(v) => {
                    setListVariable(v);
                    setListCustomVar('');
                    if (v === 'questions') {
                      setListEmptyText('No questions included for this briefing.');
                    }
                  }}
                  onCustomChange={setListCustomVar}
                />

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-foreground">
                    How should each item look?
                  </Label>
                  <Select value={listFormat} onValueChange={setListFormat}>
                    <SelectTrigger
                      className="h-9 text-sm"
                      data-testid={`${testId}-logic-format`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LIST_FORMATS.map((f) => (
                        <SelectItem key={f.value} value={f.value} className="text-sm">
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <pre className="whitespace-pre-wrap rounded-md bg-muted/60 px-2.5 py-2 text-[11px] text-muted-foreground">
                    {formatExample}
                  </pre>
                </div>
              </div>

              <div className="space-y-2 rounded-lg border border-dashed border-l-4 border-l-muted-foreground/40 p-3">
                <Label className="text-xs font-medium text-foreground">
                  Otherwise — if the list is empty, show this
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Leave blank to show nothing when there are no items.
                </p>
                <Textarea
                  rows={2}
                  value={listEmptyText}
                  onChange={(e) => setListEmptyText(e.target.value)}
                  placeholder="e.g. No questions included for this briefing."
                  className="text-sm"
                  data-testid={`${testId}-logic-else`}
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-xs text-destructive" data-testid={`${testId}-logic-error`}>
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={insert}
            data-testid={`${testId}-logic-insert`}
          >
            Insert into template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
